import type { UploadProps } from 'antd';
import { message, Upload } from 'antd';
import type { RcFile } from 'antd/es/upload';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from '../../../store/user/userSlice';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UploadFileStatus } from 'antd/es/upload/interface';
import {
    ACCEPTED_IMAGE_FORMATS,
    ALLOWED_IMAGE_TYPES,
} from '@src/constants/upload';
import { INPUT_CONFIG } from '@src/constants/inputConfig';

interface UseUploadProps extends UploadProps {
    format?: string;
    uploadPath?: string;
    hideNotifications?: boolean;
    src?: string;
    name?: string;
    callBack?: (info: any) => void;
    onRemove?: (file: any) => void;
}

/**
 * Custom hook for handling file uploads with validation
 * @param props - Upload configuration props
 * @important externalBeforeUpload should be memoized (e.g., with useCallback) in the parent component to avoid unnecessary re-renders
 */
export function useUpload(props: UseUploadProps) {
    const { t } = useTranslation();
    const user = useSelector(selectCurrentUser);
    const [uploading, setUploading] = useState(false);

    const {
        format,
        callBack,
        onRemove,
        uploadPath = '/uploads/s3',
        hideNotifications,
        src,
        name = 'file',
        beforeUpload: externalBeforeUpload,
        ...rest
    } = props;

    const acceptedFormats = format || ACCEPTED_IMAGE_FORMATS;
    const allowedTypesString = ALLOWED_IMAGE_TYPES.join(', ');

    const formatPercent = (percent?: number) =>
        percent ? `${parseFloat(percent.toFixed(2))}%` : '';

    const defaultFile = useMemo(() => {
        if (!src) return undefined;

        return [
            {
                uid: src,
                name: src.split('/').pop() || 'file',
                status: 'done' as UploadFileStatus,
                url: src,
                response: { url: src, key: src },
            },
        ];
    }, [src]);

    const beforeUpload = useCallback(
        async (
            file: RcFile,
            fileList: RcFile[]
        ): Promise<boolean | typeof Upload.LIST_IGNORE> => {
            if (externalBeforeUpload) {
                const externalResult = await externalBeforeUpload(
                    file,
                    fileList
                );
                if (
                    externalResult === false ||
                    externalResult === Upload.LIST_IGNORE
                ) {
                    return externalResult;
                }
            }

            if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
                message.error(
                    t('errors.upload.invalidType', {
                        types: allowedTypesString,
                        defaultValue: 'Only {{types}} formats are allowed.',
                    })
                );
                return Upload.LIST_IGNORE;
            }

            if (file.size > INPUT_CONFIG.UPLOAD.MAX_FILE_SIZE_BYTES) {
                message.error(
                    t('errors.upload.fileTooLarge', {
                        size: INPUT_CONFIG.UPLOAD.MAX_FILE_SIZE_MB,
                    })
                );
                return Upload.LIST_IGNORE;
            }

            // Validate image dimensions for raster images (PNG, JPEG)
            // SVG files are vector-based and don't need pixel dimension validation
            const isRasterImage =
                file.type === 'image/png' || file.type === 'image/jpeg';
            if (isRasterImage) {
                const dimensionsValid = await new Promise<boolean>(
                    (resolve) => {
                        const img = new Image();
                        const objectUrl = URL.createObjectURL(file);
                        let timeoutId: NodeJS.Timeout | null = null;

                        const cleanUp = () => {
                            URL.revokeObjectURL(objectUrl);
                            if (timeoutId) clearTimeout(timeoutId);
                        };

                        timeoutId = setTimeout(() => {
                            message.error(
                                t('errors.upload.imageLoadTimeout', {
                                    defaultValue:
                                        'The image could not be loaded in time. Please check the file and try again.',
                                })
                            );
                            resolve(false);
                            cleanUp();
                        }, INPUT_CONFIG.UPLOAD.IMAGE_LOAD_TIMEOUT_MS);

                        img.src = objectUrl;

                        img.onload = () => {
                            try {
                                const { width, height } = img;
                                const valid =
                                    width >=
                                        INPUT_CONFIG.UPLOAD.MIN_IMAGE_WIDTH &&
                                    height >=
                                        INPUT_CONFIG.UPLOAD.MIN_IMAGE_HEIGHT &&
                                    width <=
                                        INPUT_CONFIG.UPLOAD.MAX_IMAGE_WIDTH &&
                                    height <=
                                        INPUT_CONFIG.UPLOAD.MAX_IMAGE_HEIGHT;

                                if (
                                    width <
                                        INPUT_CONFIG.UPLOAD.MIN_IMAGE_WIDTH ||
                                    height <
                                        INPUT_CONFIG.UPLOAD.MIN_IMAGE_HEIGHT
                                ) {
                                    message.error(
                                        t('errors.upload.imageTooSmall', {
                                            width: INPUT_CONFIG.UPLOAD
                                                .MIN_IMAGE_WIDTH,
                                            height: INPUT_CONFIG.UPLOAD
                                                .MIN_IMAGE_HEIGHT,
                                        })
                                    );
                                } else if (
                                    width >
                                        INPUT_CONFIG.UPLOAD.MAX_IMAGE_WIDTH ||
                                    height >
                                        INPUT_CONFIG.UPLOAD.MAX_IMAGE_HEIGHT
                                ) {
                                    message.error(
                                        t('errors.upload.imageTooLarge', {
                                            width: INPUT_CONFIG.UPLOAD
                                                .MAX_IMAGE_WIDTH,
                                            height: INPUT_CONFIG.UPLOAD
                                                .MAX_IMAGE_HEIGHT,
                                        })
                                    );
                                }

                                resolve(valid);
                            } finally {
                                cleanUp();
                            }
                        };

                        img.onerror = () => {
                            message.error(
                                t('errors.upload.invalidImage', {
                                    defaultValue: 'Invalid image file.',
                                })
                            );
                            resolve(false);
                            cleanUp();
                        };
                    }
                );

                if (!dimensionsValid) {
                    return Upload.LIST_IGNORE;
                }
            }

            return true;
        },
        [externalBeforeUpload, t, allowedTypesString]
    );

    const UploadProps: UploadProps = useMemo(
        () => ({
            name,
            action: uploadPath,
            headers: {
                authorization: `Bearer ${user?.accessToken}`,
            },
            accept: acceptedFormats,
            defaultFile,
            progress: {
                strokeColor: { '0%': '#108ee9', '100%': '#87d068' },
                strokeWidth: 3,
                format: formatPercent,
            },
            beforeUpload,
            onChange(info) {
                if (info.file.status === 'uploading') {
                    setUploading(true);
                } else {
                    setUploading(false);
                }

                if (info.file.status === 'done') {
                    if (!hideNotifications) {
                        message.success({
                            content: `${info.file.name} file uploaded successfully`,
                            className: 'response-success',
                            type: 'success',
                        });
                    }
                    callBack?.(info);
                } else if (info.file.status === 'error') {
                    message.error({
                        content: `${info.file.name} file upload failed.`,
                        className: 'response-error',
                        type: 'error',
                    });
                }
            },
            onRemove(file) {
                onRemove?.(file);
            },
            ...rest,
        }),
        [
            name,
            user?.accessToken,
            uploadPath,
            acceptedFormats,
            defaultFile,
            hideNotifications,
            callBack,
            onRemove,
            beforeUpload,
            rest,
        ]
    );

    return {
        UploadProps,
        uploading,
        t,
    };
}
